import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import backgroundImg from "../assets/visitenkarteDieWerkstatt.jpeg";
import workshopData from "./data/workshops.json";
import "../css/Kontakt.css";

function Hero() {
  return (
    <header
      className="hero hero--kontakt"
      style={{ backgroundImage: `url(${backgroundImg})` }}
    >
      <div className="container hero__inner">
        <h1 className="text-align-center">KONTAKT</h1>
        <div className="contact-info">
          <p className="lead">
            DIE WERKSTATT - Dekorationen und Floristik 
          </p>
          <p className="lead">
            Sibille Frenger
          </p>
          <p className="lead">
            <a href="mailto:blumenliese@gmx.de">blumenliese@gmx.de</a>
          </p>
          <p className="lead">
            <a href="tel:+491751952530">0175/1952530</a>
          </p>
        </div>
      </div>
    </header>
  );
}

function ContactForm() {
  const [searchParams] = useSearchParams();
  const [contactType, setContactType] = useState("general");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    workshopId: "",
    agreePrivacy: false,
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // URL-Parameter beim Laden auslesen und Formular vorausfüllen
  useEffect(() => {
    const type = searchParams.get("type");
    const workshopId = searchParams.get("workshopId");

    if (type === "workshop" && workshopId) {
      setContactType("workshop");
      setFormData((prev) => ({
        ...prev,
        workshopId: workshopId,
      }));
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreePrivacy) {
      setSubmitStatus({
        type: "error",
        message: "Bitte akzeptiere die Datenschutzerklärung.",
      });
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({
        type: "error",
        message: "Bitte fülle alle erforderlichen Felder aus.",
      });
      return;
    }

    if (contactType === "workshop" && !formData.workshopId) {
      setSubmitStatus({
        type: "error",
        message: "Bitte wähle einen Workshop aus.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let response;

      // Unterschiedliche Endpoints basierend auf Anfrage-Typ
      if (contactType === "workshop") {
        // 📋 WORKSHOP-ANMELDUNG → send-workshop-email.js
        const workshopPayload = {
          kunde_email: formData.email,
          kunde_name: formData.name,
          telefon: formData.phone || "",
          nachricht: formData.message,
          workshop: selectedWorkshop, // Ganze Workshop-Objekt senden
        };

        response = await fetch("/.netlify/functions/send-workshop-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(workshopPayload),
        });
      } else {
        // 💬 ALLGEMEINE ANFRAGE → send-contact-email.js
        const contactPayload = {
          kunde_email: formData.email,
          kunde_name: formData.name,
          telefon: formData.phone || "",
          nachricht: formData.message,
        };

        response = await fetch("/.netlify/functions/send-contact-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactPayload),
        });
      }

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        setSubmitStatus({
          type: "success",
          message: "Deine Anfrage wurde erfolgreich versendet. Vielen Dank!",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
          workshopId: "",
          agreePrivacy: false,
        });
      } else {
        setSubmitStatus({
          type: "error",
          message: responseData.error || "Ein Fehler ist aufgetreten. Bitte versuche es später erneut.",
        });
      }
    } catch (error) {
      console.error("Fehler beim Absenden:", error);
      setSubmitStatus({
        type: "error",
        message: "Ein Fehler ist aufgetreten. Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedWorkshop = formData.workshopId
    ? workshopData.find((w) => w.id === formData.workshopId)
    : null;

  return (
    <main className="section section--page">
      <div className="narrow">
        <h2 className="page-title">Kontaktformular</h2>
        <p className="intro">
          Bitte wähle die Art deiner Anfrage und fülle das untenstehende Formular aus.
        </p>

        <form onSubmit={handleSubmit} className="contact-form">
          {/* Kontakttyp-Auswahl */}
          <div className="form-group form-group--row">
            <label>
              <input
                type="radio"
                name="contactType"
                value="general"
                checked={contactType === "general"}
                onChange={(e) => setContactType(e.target.value)}
              />
              <span>Allgemeine Kontaktanfrage</span>
            </label>
            <label>
              <input
                type="radio"
                name="contactType"
                value="workshop"
                checked={contactType === "workshop"}
                onChange={(e) => setContactType(e.target.value)}
              />
              <span>Workshopanmeldung anfragen</span>
            </label>
          </div>

          {/* Workshop-Auswahl (nur bei Workshopanmeldung) */}
          {contactType === "workshop" && (
            <div className="form-group">
              <label htmlFor="workshopId">Workshop auswählen *</label>
              <select
                id="workshopId"
                name="workshopId"
                value={formData.workshopId}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Bitte Workshop auswählen --</option>
                {workshopData.map((workshop) => (
                  <option key={workshop.id} value={workshop.id}>
                    {workshop.ort} - {workshop.datum}
                  </option>
                ))}
              </select>
              {selectedWorkshop && (
                <p className="workshop-selected-info">
                  Gewählter Workshop: <strong>{selectedWorkshop.ort}</strong> am{" "}
                  <strong>{selectedWorkshop.datum}</strong>
                </p>
              )}
            </div>
          )}

          {/* Name */}
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Ihr Name"
              required
            />
          </div>

          {/* E-Mail */}
          <div className="form-group">
            <label htmlFor="email">E-Mail *</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="ihre.email@example.com"
              required
            />
          </div>

          {/* Telefon */}
          <div className="form-group">
            <label htmlFor="phone">Telefon (optional)</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="0175/1952530"
            />
          </div>

          {/* Nachricht */}
          <div className="form-group">
            <label htmlFor="message">Nachricht *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Ihre Nachricht..."
              rows="6"
              required
            />
          </div>

          {/* Datenschutz-Checkbox */}
          <div className="form-group form-group--checkbox">
            <label>
              <input
                type="checkbox"
                name="agreePrivacy"
                checked={formData.agreePrivacy}
                onChange={handleInputChange}
                required
              />
              <span>
                Ich habe die{" "}
                <Link to="/datenschutz" className="privacy-link">
                  Datenschutzerklärung
                </Link>{" "}
                zur Kenntnis genommen und ich erkläre mich damit einverstanden *
              </span>
            </label>
          </div>

          {/* Button */}
          <button
            type="submit"
            className="btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Wird versendet..." : "Absenden"}
          </button>
        </form>

        {submitStatus && (
          <div style={{ marginTop: "24px" }}>
            <div
              className={`contact-status contact-status--${submitStatus.type}`}
              role="alert"
            >
              {submitStatus.message}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default function Kontakt() {
  return (
    <>
      <Hero />
      <ContactForm />
    </>
  );
}
